import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Modal } from "@/components/ui/Modal";
import { Select, Textarea } from "@/components/ui/Select";
import { Button, Input, Label } from "@/design-system/components/ui";
import { UserPicker } from "@/components/users/UserPicker";
import { useBuyerTeams } from "@/hooks/buyers/useBuyerTeams";
import type { LoanCommissionAttributionDto } from "@/api/loanCommissionAttributions";
import {
  useCorrectLoanCommissionAttribution,
  useLoanCommissionAttribution,
} from "@/hooks/commissions/useLoanCommissionAttribution";
import { datetime } from "@/components/commissions/formatters";
import { HELD_REASON_COPY } from "@/components/commissions/ClaimHeld";
import { useDateFormatter } from "@/utils/datetime";

/**
 * L7/U7 — the ADMIN correction of one loan's frozen commission attribution.
 *
 * <p>🔴 <b>THE ONE DOOR TO A STAMPED ROW, and the only thing that clears a
 * STAMPED held reason.</b> The rest of the module reads this row verbatim,
 * which is what keeps an F1/F2 share stable for the life of the loan and is
 * also what makes a wrong stamp permanent without this screen. Every held-claim
 * note in the product already tells a reviewer to come here.</p>
 *
 * <p>⚠️ <b>A FULL REPLACE, NOT A PATCH — which is why every field is hydrated
 * before it is offered.</b> The request states what the row IS; an empty field
 * means null. A patch with a clear flag per field would make one screen produce
 * two different rows depending on which boxes were touched, and would leave the
 * attestation unable to say which. The attestation records the whole before and
 * the whole after, so the assertion an ADMIN is signing is "this is the row",
 * not "this is my delta".</p>
 *
 * <p>🔴 <b>Every value shows its SOURCE.</b> Overruling the assignment ledger,
 * overruling a backfill's inference, and overruling an earlier correction are
 * three different decisions, and the ADMIN cannot tell them apart from the
 * values alone.</p>
 */
export function CorrectAttributionModal({
  loanId,
  open,
  onClose,
}: {
  loanId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { tz } = useDateFormatter();
  const attribution = useLoanCommissionAttribution(loanId);
  const correct = useCorrectLoanCommissionAttribution(loanId);
  const row = attribution.data;

  // Archived teams included: an attribution legitimately names a team that has
  // since been archived, and a picker that cannot offer it back cannot restate
  // the row it is editing.
  const teams = useBuyerTeams(true);

  const [reason, setReason] = useState("");
  const [form, setForm] = useState<FormState>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  // 🔴 SEEDED ONCE PER LOADED ROW, keyed on the row's identity rather than on
  // the object — the mutation invalidates the whole commissions prefix, so this
  // query refetches on success and an effect keyed on `row` would re-hydrate
  // over whatever the ADMIN had typed if she reopened without the dialog
  // unmounting. `loanId` plus stamped-ness is the identity that matters here.
  const seedKey = row ? `${row.loanId}:${row.isStamped}` : null;
  useEffect(() => {
    if (!row) return;
    setForm(hydrate(row));
    setReason("");
    setError(null);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const teamOptions = useMemo(() => {
    const list = (teams.data ?? []).map((t) => ({ id: t.id, name: t.name }));
    // The row's own team, when the list does not carry it — a team that was
    // hard-deleted, or a list the caller cannot see in full. Losing it from the
    // picker would silently blank the field on save.
    if (form.teamId && !list.some((t) => t.id === form.teamId)) {
      list.push({
        id: form.teamId,
        name: row?.attributedSpecialistTeamName ?? "(team not in list)",
      });
    }
    return list;
  }, [teams.data, form.teamId, row?.attributedSpecialistTeamName]);

  async function submit() {
    setError(null);
    if (reason.trim().length === 0) {
      setError("Say why this attribution is being corrected.");
      return;
    }
    // A GUID typed by hand is the one field with no picker behind it, so it is
    // the one field that can be malformed. Refusing here rather than at the
    // server keeps the ADMIN's other typing on screen.
    if (form.buyerLeadId.trim() !== "" && !GUID.test(form.buyerLeadId.trim())) {
      setError("The buyer lead id is not a valid id.");
      return;
    }
    try {
      const r = await correct.mutateAsync({
        reason: reason.trim(),
        buyerLeadId: blank(form.buyerLeadId),
        attributedSpecialistUserId: blank(form.specialistUserId),
        attributedSpecialistTeamId: blank(form.teamId),
        teamF1UserId: blank(form.teamF1UserId),
        brokerLiaisonUserId: blank(form.brokerLiaisonUserId),
        brokerSideF1UserId: blank(form.brokerSideF1UserId),
        brokerSideF1EffectiveFrom: blank(form.brokerSideF1EffectiveFrom),
      });
      // 🔴 THE LOCKED COUNT IS REPORTED, NEVER SWALLOWED. Those months still
      // carry the old apportionment and an unlock is the only thing that
      // changes them, so a bare "Saved" would tell an ADMIN the correction
      // reached money it did not reach.
      setResult(
        `${r.claimsMoved} claim${r.claimsMoved === 1 ? "" : "s"} re-routed · ` +
          `${r.rollupPeriodsRedrafted} monthly roll-up${r.rollupPeriodsRedrafted === 1 ? "" : "s"} redrafted` +
          (r.rollupPeriodsLocked > 0
            ? ` · ${r.rollupPeriodsLocked} month${r.rollupPeriodsLocked === 1 ? "" : "s"} skipped — the accounting period is locked, so ${r.rollupPeriodsLocked === 1 ? "it keeps" : "they keep"} the old apportionment until unlocked`
            : ""),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "The correction could not be saved.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Correct commission attribution"
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={correct.isPending || !row}>
              {correct.isPending ? "Saving…" : "Save correction"}
            </Button>
          )}
        </div>
      }
    >
      {attribution.isLoading && <p className="text-small text-gray-mid">Loading…</p>}

      {attribution.isError && (
        <p role="alert" className="text-small text-status-error">
          This loan's attribution could not be loaded.
        </p>
      )}

      {row && (
        <div className="space-y-space-4">
          <p className="text-small text-gray-mid">
            {row.isStamped
              ? "This loan's attribution was frozen when it closed. Correcting it re-routes every F1 and F2 share on the loan that is still suggested, and redrafts the monthly roll-ups it contributed to. Nothing past Suggested is touched."
              : "This loan has no commission attribution at all, so every book share on it is held. Filling this in creates one and routes the shares that are still suggested."}
          </p>

          {row.heldReason && (
            <p className="text-small text-status-warning">
              {/* Held reasons are stored codes; show the label, never the code.
                  A code newer than this bundle falls back to itself. */}
              Currently held — {HELD_REASON_COPY[row.heldReason]?.label ?? row.heldReason}
              {row.heldReasonDetail ? `: ${row.heldReasonDetail}` : ""}
            </p>
          )}

          <div>
            <Label htmlFor="attr-reason" required>
              Why
            </Label>
            <Textarea
              id="attr-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What was wrong, and what you checked."
            />
          </div>

          <Field label="Buyer lead id" source={row.buyerLeadIdSource}>
            <Input
              aria-label="Buyer lead id"
              value={form.buyerLeadId}
              onChange={(e) => setForm({ ...form, buyerLeadId: e.target.value })}
              placeholder="Empty means this loan has no buyer lead"
            />
          </Field>

          <Field label="Attributed consultant" source={row.attributedSpecialistSource}>
            <UserPicker
              ariaLabel="Attributed consultant"
              value={blank(form.specialistUserId)}
              initialDisplayName={row.attributedSpecialistName ?? undefined}
              allowUnassigned
              onChange={(id) => setForm({ ...form, specialistUserId: id ?? "" })}
            />
          </Field>

          <Field label="Consultant's team" source={row.teamSource}>
            <Select
              aria-label="Consultant's team"
              value={form.teamId}
              onChange={(e) => setForm({ ...form, teamId: e.target.value })}
            >
              <option value="">— none —</option>
              {teamOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Team F1 at the attributing assignment" source={row.teamF1Source}>
            <UserPicker
              ariaLabel="Team F1"
              value={blank(form.teamF1UserId)}
              initialDisplayName={row.teamF1Name ?? undefined}
              allowUnassigned
              onChange={(id) => setForm({ ...form, teamF1UserId: id ?? "" })}
            />
          </Field>

          <Field label="Broker liaison at closing" source={row.brokerLiaisonSource}>
            <UserPicker
              ariaLabel="Broker liaison"
              value={blank(form.brokerLiaisonUserId)}
              initialDisplayName={row.brokerLiaisonName ?? undefined}
              allowUnassigned
              onChange={(id) => setForm({ ...form, brokerLiaisonUserId: id ?? "" })}
            />
          </Field>

          <Field label="Broker-side F1" source={row.brokerSideF1Source}>
            <UserPicker
              ariaLabel="Broker-side F1"
              value={blank(form.brokerSideF1UserId)}
              initialDisplayName={row.brokerSideF1Name ?? undefined}
              allowUnassigned
              onChange={(id) => setForm({ ...form, brokerSideF1UserId: id ?? "" })}
            />
          </Field>

          <Field label="Broker-side F1 effective from" source={null}>
            <Input
              type="date"
              aria-label="Broker-side F1 effective from"
              value={form.brokerSideF1EffectiveFrom}
              onChange={(e) =>
                setForm({ ...form, brokerSideF1EffectiveFrom: e.target.value })
              }
            />
            <p className="mt-space-1 text-caption text-gray-mid">
              U3 — income recognised before this day stays outside her F1 book.
              Empty means the broker-side share runs from the start.
            </p>
          </Field>

          {row.corrections.length > 0 && (
            <div>
              <Label>Earlier corrections</Label>
              <ul className="text-caption text-gray-mid space-y-space-1">
                {row.corrections.map((c) => (
                  <li key={c.id}>
                    {datetime(c.attestedAt, tz)} — {c.attestedByName ?? c.attestedByUserId ?? "unknown"}:{" "}
                    {c.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <p role="alert" className="text-small text-status-error">
              {error}
            </p>
          )}
          {result && (
            <p role="status" className="text-small text-status-success">
              {result}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** A labelled row that always shows where its current value came from. */
function Field({
  label,
  source,
  children,
}: {
  label: string;
  /** `null` for a field that carries no provenance of its own. */
  source: string | null | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <Label>
        {label}
        {source && (
          <span className="ml-space-2 font-sans font-normal normal-case tracking-normal text-gray-mid">
            from {source}
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}

type FormState = {
  buyerLeadId: string;
  specialistUserId: string;
  teamId: string;
  teamF1UserId: string;
  brokerLiaisonUserId: string;
  brokerSideF1UserId: string;
  brokerSideF1EffectiveFrom: string;
};

const BLANK: FormState = {
  buyerLeadId: "",
  specialistUserId: "",
  teamId: "",
  teamF1UserId: "",
  brokerLiaisonUserId: "",
  brokerSideF1UserId: "",
  brokerSideF1EffectiveFrom: "",
};

/** 🔴 Hydrates EVERY field, including the ones nobody is going to touch. The
 *  request is a full replace, so a field the form left blank is a field the
 *  save nulls. */
function hydrate(row: LoanCommissionAttributionDto): FormState {
  return {
    buyerLeadId: row.buyerLeadId ?? "",
    specialistUserId: row.attributedSpecialistUserId ?? "",
    teamId: row.attributedSpecialistTeamId ?? "",
    teamF1UserId: row.teamF1UserId ?? "",
    brokerLiaisonUserId: row.brokerLiaisonUserId ?? "",
    brokerSideF1UserId: row.brokerSideF1UserId ?? "",
    brokerSideF1EffectiveFrom: row.brokerSideF1EffectiveFrom ?? "",
  };
}

const blank = (v: string) => (v.trim() === "" ? null : v.trim());
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
